import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

interface NavItem {
  label: string;
  to: string;
}

const navItems: NavItem[] = [
  { label: "主页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tvshow" },
  { label: "综艺", to: "/category/show" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname === "/search") {
      setKeyword(searchParams.get("q") ?? "");
    }
  }, [location.pathname, searchParams]);

  const submitSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex h-16 items-center gap-3 px-4 md:h-[68px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-film-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
              91蜜桃
            </span>
            <span className="hidden text-[10px] tracking-widest text-foreground-500 sm:block">
              影视资源 · 资讯平台
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto hidden items-center md:flex">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3.5 py-2 transition-colors focus-within:border-primary-400 focus-within:bg-background-50">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder="搜索电影、电视剧、综艺或资讯"
              className="w-44 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-56"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <div className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3.5 py-2 focus-within:border-primary-400">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </div>
          <nav className="grid grid-cols-2 gap-1.5">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "bg-background-100 text-foreground-800"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}